import React, { useCallback, useEffect, useRef, useState } from "react";
import CommonMovieCard from "./CommonMovieCard";
import { getCommonDivaItems, submitCommonDivaItem } from "../service/api";
import { commonErrorMessage, newClientResponseId } from "./commonSubmission";

export default function CommonDivaItemTask({ userId, commonSessionId, onPhaseChange }) {
  const [items, setItems] = useState([]);
  const [approval, setApproval] = useState("");
  const [appeal, setAppeal] = useState("");
  const [confidence, setConfidence] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [retryPayload, setRetryPayload] = useState(null);
  const headingRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCommonDivaItems({ userId, commonSessionId });
      if (response.phase !== "common_diva_items_in_progress") { onPhaseChange(response.phase, commonSessionId); return; }
      setItems(response.items);
      setError("");
    } catch (requestError) { setError(commonErrorMessage(requestError)); }
    finally { setLoading(false); }
  }, [commonSessionId, onPhaseChange, userId]);
  useEffect(() => { load(); }, [load]);

  const current = items.find((item) => !item.answered);
  const answered = items.filter((item) => item.answered).length;
  const currentMovieId = current?.movieId;
  useEffect(() => { if (currentMovieId) headingRef.current?.focus(); }, [currentMovieId]);
  const send = async (payload) => {
    setSubmitting(true); setError("");
    try {
      const receipt = await submitCommonDivaItem(payload);
      setRetryPayload(null);
      setApproval(""); setAppeal(""); setConfidence("");
      if (receipt.phase !== "common_diva_items_in_progress") onPhaseChange(receipt.phase, commonSessionId);
      else await load();
    } catch (requestError) { setRetryPayload(payload); setError(commonErrorMessage(requestError)); }
    finally { setSubmitting(false); }
  };
  const submit = (event) => {
    event.preventDefault();
    if (!approval || !appeal || !confidence) { setError("Please answer all three questions."); return; }
    send({ userId, commonSessionId, movieId: current.movieId, approval, currentAppeal: Number(appeal), itemConfidence: Number(confidence), itemPresentationOrder: current.presentationPosition, clientResponseId: newClientResponseId() });
  };
  if (loading && !items.length) return <p role="status">Loading movie judgments…</p>;
  if (!current) return <div role="alert">{error || "Checking saved movie judgments…"}<button type="button" onClick={load}>Try again</button></div>;
  return (
    <section className="common-diva-item">
      <h2 ref={headingRef} tabIndex="-1">Movie judgments</h2>
      <p className="common-progress">Movie judgments {answered + 1} / 7</p>
      <CommonMovieCard movie={current.movie} compact />
      <form onSubmit={submit}>
        <fieldset><legend>Would you actually consider choosing this movie to watch or rewatch if it were available now?</legend>
          <label><input type="radio" name="approval" value="yes" checked={approval === "yes"} onChange={(e) => setApproval(e.target.value)} /> Yes</label>
          <label><input type="radio" name="approval" value="no" checked={approval === "no"} onChange={(e) => setApproval(e.target.value)} /> No</label>
        </fieldset>
        <label>How appealing is this movie to you as something to watch or rewatch?
          <span className="scale-labels"><span>1 = Not at all appealing</span><span>7 = Extremely appealing</span></span>
          <select value={appeal} onChange={(e) => setAppeal(e.target.value)} required><option value="">Choose 1–7</option>{[1,2,3,4,5,6,7].map((n) => <option key={n}>{n}</option>)}</select>
        </label>
        <label>How confident are you in these judgments about this movie?
          <span className="scale-labels"><span>1 = Not at all confident</span><span>5 = Extremely confident</span></span>
          <select value={confidence} onChange={(e) => setConfidence(e.target.value)} required><option value="">Choose 1–5</option>{[1,2,3,4,5].map((n) => <option key={n}>{n}</option>)}</select>
        </label>
        {error && <div className="common-error" role="alert">{error}</div>}
        {retryPayload ? <button type="button" disabled={submitting} onClick={() => send(retryPayload)}>Retry the same response</button> : <button type="submit" disabled={submitting}>Save and continue</button>}
      </form>
    </section>
  );
}
